import { Injectable } from '@angular/core';
import { TopicLevel2Service } from '@examdojo/category/v2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PracticeActivityService, PracticeActivityStoreModel } from '@examdojo/practice-activity';
import { QuestionAttemptEntityQuery, QuestionAttemptEntityService } from '@examdojo/question';
import { catchError, EMPTY, forkJoin, switchMap } from 'rxjs';
import { StreakService } from '@examdojo/streak';
import { TopicPracticeDialogService } from '../features/practice/topic-practice/dialog/topic-practice-dialog.service';
import { ExamdojoPlatformService } from '../shared/platform.service';
import { UserMarksService } from '../user/mark/user-marks.service';
import { AssessmentQuery } from '../features/assessments/assessment.query';

@Injectable()
export class SyncPracticeActivityStateService {
  constructor(
    private readonly practiceActivityService: PracticeActivityService,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly topicPracticeDialogService: TopicPracticeDialogService,
    private readonly userMarksService: UserMarksService,
    private readonly platformService: ExamdojoPlatformService,
    private readonly streakService: StreakService,
    private readonly topicLevel2Service: TopicLevel2Service,
    private readonly assessmentQuery: AssessmentQuery,
  ) {
    this.syncStoreWhenBackOnline().pipe(takeUntilDestroyed()).subscribe();
  }

  private syncStoreWhenBackOnline() {
    return this.platformService.trigger$.pipe(
      switchMap(() =>
        forkJoin([
          this.practiceActivityService.fetchAll(),
          this.userMarksService.fetchLastWeekUserMarks(),
          this.userMarksService.getUserSpentPointsAndLimits(),
          this.streakService.fetchStreaks(),
          this.topicLevel2Service.fetchProficiencies(),
          this.topicLevel2Service.fetchPredictedScore(
            this.assessmentQuery.getUIEntity(this.assessmentQuery.getActiveId()!)?.topicLevel2Ids ?? [],
          ),
        ]).pipe(
          switchMap(([practiceActivities]) => {
            if (!practiceActivities.length && this.topicPracticeDialogService.isOpen()) {
              this.practiceActivityService.setCandidates(null);
              return this.topicPracticeDialogService.dismissPracticeActivityDialog();
            }

            return this.syncQuestionAttemptStoreIfAnyChangeHasHappened(practiceActivities);
          }),

          catchError(() => EMPTY),
        ),
      ),
    );
  }

  private syncQuestionAttemptStoreIfAnyChangeHasHappened(practiceActivities: PracticeActivityStoreModel[]) {
    const currentQuestionAttempt = this.questionAttemptQuery.getActive()?.attempt;

    if (
      practiceActivities &&
      practiceActivities[0] &&
      !!practiceActivities[0].question_attempt_id &&
      !!currentQuestionAttempt &&
      practiceActivities[0].question_attempt_id === currentQuestionAttempt.id
    ) {
      return this.questionAttemptService.fetchQuestionAttempt(practiceActivities[0].question_attempt_id).pipe(
        switchMap((fetchedAttempt) => {
          if (fetchedAttempt?.updated_at !== currentQuestionAttempt.updated_at) {
            return this.questionAttemptService.fetchQuestionAttemptDetails(currentQuestionAttempt.id, {
              setActive: true,
            });
          }
          return EMPTY;
        }),
      );
    }

    return EMPTY;
  }
}
